import React, { useMemo } from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import useBonusCellBonusProgram from '../hooks/useBonusCellBonusProgram';
import GridCellErrorBoundary from '../../GridCellErrorBoundary';
import useValueFormatters from '../../../hooks/useValueFormatters';
import Cell from '../../V5Gadget/Matrix/Cell';
import resolveExpectationField from '../utils/resolveExpectationField';
import computeExpectationIsPassed from '../utils/computeExpectationIsPassed';
import ExpectationValue from './ExpectationValue';

const ExpectationCell = ({
  elasticDocument,
  actualField,
  expectationField,
  isPassed,
  dataset,
}: {
  elasticDocument: ElasticDocument;
  actualField: string;
  expectationField: string;
  isPassed: boolean;
  dataset: string;
}) => {
  const { formatField } = useValueFormatters();
  const formattedExpectation = useMemo(() => {
    return formatField({
      value: elasticDocument[expectationField],
      dataset,
      field: expectationField,
    });
  }, [dataset, elasticDocument, expectationField, formatField]);
  const formattedActual = useMemo(() => {
    return formatField({
      value: elasticDocument[actualField],
      dataset,
      field: actualField,
    });
  }, [actualField, dataset, elasticDocument, formatField]);

  return (
    <Cell rightAlign>
      <ExpectationValue
        actual={formattedActual}
        target={formattedExpectation}
        isPassed={isPassed}
      />
    </Cell>
  );
};

/**
 * Shows the progress of a given expectation,
 * eg: for the field totalMilesTarget, show 9400 / 10000
 */
const ExpectationCellProvider = (params: ICellRendererParams) => {
  const bonusProgram = useBonusCellBonusProgram();

  const elasticDocument = useMemo(() => {
    return params.node.data as ElasticDocument;
  }, [params]);

  const field = useMemo(() => {
    const { colDef } = params;
    if (!colDef || !colDef.field) {
      return undefined;
    }

    return colDef.field;
  }, [params]);

  const criteriaField = useMemo(() => {
    if (!field) {
      return undefined;
    }

    return resolveExpectationField({ program: bonusProgram, field });
  }, [bonusProgram, field]);

  const status = useMemo((): 'Passed' | 'Failed' | 'On Track' | undefined => {
    if (!criteriaField) {
      return undefined;
    }

    return elasticDocument[criteriaField.statusField];
  }, [criteriaField, elasticDocument]);

  const belowTargetIsGood = useMemo((): boolean | undefined => {
    return params.colDef?.cellRendererParams?.fieldView?.belowTargetIsGood;
  }, [params]);

  const isPassed = useMemo((): boolean | undefined => {
    return computeExpectationIsPassed({ status, belowTargetIsGood });
  }, [status, belowTargetIsGood]);

  if (
    !bonusProgram ||
    !field ||
    isPassed === undefined ||
    !criteriaField ||
    params.node.rowPinned
  ) {
    return null;
  }

  const { actualField, targetField } = criteriaField;

  return (
    <ExpectationCell
      elasticDocument={elasticDocument}
      dataset={bonusProgram.dataset}
      actualField={actualField}
      expectationField={targetField}
      isPassed={isPassed}
    />
  );
};

const Gate = (params: ICellRendererParams) => (
  <GridCellErrorBoundary
    params={params}
    childComponent={<ExpectationCellProvider {...params} />}
  />
);

export default Gate;
